<template>
  <div class="location-dropdown">
    <div class="location-dropdown-toggle" @click="toggleDropdown">
      <div class="d-flex">
        <span><i class="fas fa-map-marker-alt pt-1"></i></span>
        <span class="pl-2">
          <h4 class="h5 mb-0">{{ displayCity || 'Enter Your Location' }}</h4>
          <p class="small mb-0">{{ displayAddress || 'Location Info' }}</p>
        </span>
      </div>
    </div>

    <div v-if="showDropdown" class="location-dropdown-menu">

      <div class="location-actions">
        <button class="btn-current" @click="useCurrentLocation">
          <i class="fas fa-crosshairs mr-2"></i> Use Current Location
        </button>
      </div>

      <div class="search-wrapper">
        <i class="fas fa-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          @input="fetchSuggestions"
          placeholder="Search for a city or address"
        />
      </div>

      <div class="suggestions" v-if="suggestions.length">
        <div
          class="suggestion-item"
          v-for="item in suggestions"
          :key="item.place_id"
          @click="selectSuggestion(item.place_id)"
        >
          <i class="fas fa-location-arrow mr-2"></i>{{ item.description }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global google, defineEmits */
import { ref, onMounted } from 'vue'

const emit = defineEmits(['location-selected'])

const showDropdown = ref(false)
const searchQuery = ref('')
const suggestions = ref([])
const displayCity = ref('')
const displayAddress = ref('')

const selectedLat = ref(null)
const selectedLng = ref(null)
const isLocationChangedByUser = ref(false)

let autocompleteService = null
let geocoder = null
let placesService = null

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
  if (!showDropdown.value) {
    suggestions.value = []
  }
}

const useCurrentLocation = () => {
  if (!navigator.geolocation) {
    alert('Geolocation not supported')
    return
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const { latitude, longitude } = position.coords
      reverseGeocode(latitude, longitude, false)
    },
    () => alert('Location access denied.')
  )
}

const fetchSuggestions = () => {
  if (!searchQuery.value || !autocompleteService) return
  autocompleteService.getPlacePredictions({ input: searchQuery.value }, (predictions, status) => {
    suggestions.value = status === 'OK' ? predictions : []
  })
}

const selectSuggestion = (placeId) => {
  if (!placesService) return
  placesService.getDetails({ placeId }, (place, status) => {
    if (status === 'OK' && place.geometry) {
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      const address = place.formatted_address
      const country = place.address_components?.find(x => x.types.includes('country'))?.long_name || ''
      const city = place.address_components?.find(x => x.types.includes('locality'))?.long_name || place.name || ''

      emitLocation(lat, lng, address, city, true, country, false)
    }
  })
}

const reverseGeocode = (lat, lng, userChanged = false) => {
  if (!geocoder) return
  geocoder.geocode({ location: { lat, lng } }, (results, status) => {
    if (status === 'OK' && results[0]) {
      const address = results[0].formatted_address
      const city = results[0].address_components.find(x => x.types.includes('locality'))?.long_name || ''
      const country = results[0].address_components.find(x => x.types.includes('country'))?.long_name || ''
      emitLocation(lat, lng, address, city || country, userChanged, country, false)
    }
  })
}

const emitLocation = (lat, lng, address, displayLabel, userChanged, country = '', isCountryOnly = false) => {
  selectedLat.value = lat
  selectedLng.value = lng
  isLocationChangedByUser.value = userChanged

  displayCity.value = displayLabel
  displayAddress.value = address

  localStorage.setItem('lat', lat)
  localStorage.setItem('lng', lng)
  localStorage.setItem('address', address)
  localStorage.setItem('city', displayLabel)
  localStorage.setItem('isLocationChangedByUser', userChanged)

  emit('location-selected', {
    lat,
    lng,
    country,
    address,
    userChanged,
    isCountryOnly
  })

  showDropdown.value = false
}

onMounted(() => {
  if (typeof google !== 'undefined' && google.maps) {
    autocompleteService = new google.maps.places.AutocompleteService()
    geocoder = new google.maps.Geocoder()
    placesService = new google.maps.places.PlacesService(document.createElement('div'))

    const lat = parseFloat(localStorage.getItem('lat'))
    const lng = parseFloat(localStorage.getItem('lng'))
    const isChanged = localStorage.getItem('isLocationChangedByUser') === 'true'

    if (lat && lng && isChanged) {
      reverseGeocode(lat, lng, true)
    } else {
      useCurrentLocation()
    }
  } else {
    console.error('Google Maps API not loaded')
  }
})
</script>



<style scoped>
.location-dropdown {
  position: relative;
}

.location-dropdown-toggle {
  padding: 14px;
  background: #f8fafc;
  border: 1px solid rgba(15,23,42,0.12);
  border-radius: 12px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.location-dropdown-toggle:hover {
  border-color: rgba(15,23,42,0.25);
  box-shadow: 0 8px 24px rgba(15,23,42,0.08);
}

.location-dropdown-menu {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  width: 100%;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 25px 45px rgba(15,23,42,0.15);
  padding: 16px;
  z-index: 999;
}

.location-actions {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}
.btn-current {
  border: none;
  background: #10172a;
  color: #fff;
  padding: 10px 16px;
  border-radius: 999px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.btn-current:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(16,23,42,0.25);
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid rgba(15,23,42,0.15);
  border-radius: 12px;
  padding: 0 12px;
  background: #f8fafc;
}
.search-wrapper i {
  color: #64748b;
}
.search-wrapper input {
  border: none;
  background: transparent;
  width: 100%;
  padding: 12px 8px;
  font-size: 14px;
  outline: none;
}

.suggestions {
  margin-top: 12px;
  max-height: 220px;
  overflow-y: auto;
}
.suggestion-item {
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #1e293b;
  gap: 8px;
  transition: background 0.15s ease, transform 0.15s ease;
}
.suggestion-item:hover {
  background: rgba(99,102,241,0.08);
  transform: translateX(2px);
}

@media (max-width: 480px) {
  .location-dropdown-menu {
    padding: 14px;
    border-radius: 14px;
  }
  .btn-current {
    width: 100%;
    justify-content: center;
  }
}
</style>
